/**
 * ContributorManagement Component
 *
 * Manages discussion contributors - invitations, acceptance, and listing.
 *
 * Story FOS-4.2.4: Leaf Components Batch 2 (Collaboration)
 * Task 1: Copy Collaboration Components
 *
 * Features:
 * - Invite contributor by principal ID
 * - Show pending invitations
 * - Accept/decline invitation flow
 * - Display contributor list
 *
 * @module components/governance/ContributorManagement
 * @see AC-4.2.4.3 - ContributorManagement Component Migrated
 */

import { useState, useCallback } from 'react';
import { useStore } from '@nanostores/react';
import { UserPlus, Users, Check, X, Clock, Crown, Mail } from 'lucide-react';
import { $participantsList, type DiscussionParticipant } from '../../stores/discussionStore';

/**
 * Invitation status
 */
export type InvitationStatus = 'pending' | 'accepted' | 'declined';

/**
 * Invitation data
 */
export interface ContributorInvitation {
  id: string;
  principalId: string;
  displayName?: string;
  status: InvitationStatus;
  invitedAt: number;
}

/**
 * Props for ContributorManagement component
 */
export interface ContributorManagementProps {
  /** Discussion ID */
  discussionId: string;
  /** Whether the current user is the proposer */
  isProposer: boolean;
  /** Participants list (optional, uses store if not provided) */
  participants?: DiscussionParticipant[];
  /** Pending invitations */
  invitations?: ContributorInvitation[];
  /** Callback to invite a contributor */
  onInvite?: (principalId: string) => Promise<void>;
  /** Callback to accept an invitation */
  onAccept?: (invitationId: string) => Promise<void>;
  /** Callback to decline an invitation */
  onDecline?: (invitationId: string) => Promise<void>;
  /** Whether there's a pending invitation for current user */
  pendingInvitationForUser?: ContributorInvitation | null;
  /** Optional class name */
  className?: string;
}

/**
 * Single contributor card
 */
function ContributorCard({ participant }: { participant: DiscussionParticipant }) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border"
      data-testid={`contributor-${participant.id}`}
    >
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
        {participant.isProposer ? (
          <Crown className="h-4 w-4 text-amber-500" />
        ) : (
          <Users className="h-4 w-4 text-primary" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-foreground truncate">
            {participant.displayName}
          </span>
          {participant.isProposer && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
              Proposer
            </span>
          )}
          {participant.isContributor && !participant.isProposer && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
              Contributor
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground truncate block">
          {participant.id.slice(0, 8)}...{participant.id.slice(-4)}
        </span>
      </div>
    </div>
  );
}

/**
 * Pending invitation card
 */
function InvitationCard({
  invitation,
  onCancel,
}: {
  invitation: ContributorInvitation;
  onCancel?: () => void;
}) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800"
      data-testid={`invitation-${invitation.id}`}
    >
      <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
        <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="font-medium text-sm text-foreground truncate block">
          {invitation.displayName || `${invitation.principalId.slice(0, 8)}...`}
        </span>
        <span className="text-xs text-amber-600 dark:text-amber-400">
          Invitation pending
        </span>
      </div>
      {onCancel && (
        <button
          onClick={onCancel}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
          title="Cancel invitation"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/**
 * Invite form component
 */
function InviteForm({ onInvite }: { onInvite: (principalId: string) => Promise<void> }) {
  const [principalId, setPrincipalId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!principalId.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onInvite(principalId.trim());
      setPrincipalId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  }, [principalId, onInvite]);

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="invite-form">
      <div className="flex gap-2">
        <input
          type="text"
          value={principalId}
          onChange={(e) => setPrincipalId(e.target.value)}
          placeholder="Enter principal ID..."
          className="flex-1 px-3 py-2 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="invite-input"
        />
        <button
          type="submit"
          disabled={isSubmitting || !principalId.trim()}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="invite-button"
        >
          <Mail className="h-4 w-4" />
          Invite
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400" data-testid="invite-error">
          {error}
        </p>
      )}
    </form>
  );
}

/**
 * Pending invitation banner for current user
 */
function PendingInvitationBanner({
  invitation,
  onAccept,
  onDecline,
}: {
  invitation: ContributorInvitation;
  onAccept: () => Promise<void>;
  onDecline: () => Promise<void>;
}) {
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format invitation time for display
  const invitedTimeAgo = invitation.invitedAt
    ? new Date(invitation.invitedAt).toLocaleDateString()
    : null;

  const handleAccept = async () => {
    setIsAccepting(true);
    setError(null);
    try {
      await onAccept();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDecline = async () => {
    setIsDeclining(true);
    setError(null);
    try {
      await onDecline();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setIsDeclining(false);
    }
  };

  return (
    <div
      className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800"
      data-testid="pending-invitation-banner"
    >
      <div className="flex items-center gap-3 mb-3">
        <UserPlus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <span className="font-medium text-foreground">You've been invited to contribute</span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Accept to become a contributor and help shape this proposal.
        {invitedTimeAgo && (
          <span className="block text-xs mt-1">Invited on {invitedTimeAgo}</span>
        )}
      </p>
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 mb-3" data-testid="invitation-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          onClick={handleAccept}
          disabled={isAccepting || isDeclining}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          data-testid="accept-invitation"
        >
          <Check className="h-4 w-4" />
          {isAccepting ? 'Accepting...' : 'Accept'}
        </button>
        <button
          onClick={handleDecline}
          disabled={isAccepting || isDeclining}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-muted text-muted-foreground text-sm font-medium hover:bg-muted/80 disabled:opacity-50"
          data-testid="decline-invitation"
        >
          <X className="h-4 w-4" />
          {isDeclining ? 'Declining...' : 'Decline'}
        </button>
      </div>
    </div>
  );
}

/**
 * ContributorManagement - Manage discussion contributors
 *
 * @example
 * ```tsx
 * <ContributorManagement
 *   discussionId="discussion-123"
 *   isProposer={true}
 *   onInvite={(principalId) => inviteContributor(discussionId, principalId)}
 * />
 * ```
 */
export function ContributorManagement({
  discussionId,
  isProposer,
  participants: propParticipants,
  invitations = [],
  onInvite,
  onAccept,
  onDecline,
  pendingInvitationForUser,
  className = '',
}: ContributorManagementProps) {
  const storeParticipants = useStore($participantsList);
  const participants = propParticipants ?? storeParticipants;
  const pendingInvitations = invitations.filter((i) => i.status === 'pending');

  // Suppress unused variable warning - discussionId may be used in future integrations
  void discussionId;

  return (
    <div className={`space-y-4 ${className}`} data-testid="contributor-management">
      {/* Pending invitation for current user */}
      {pendingInvitationForUser && onAccept && onDecline && (
        <PendingInvitationBanner
          invitation={pendingInvitationForUser}
          onAccept={() => onAccept(pendingInvitationForUser.id)}
          onDecline={() => onDecline(pendingInvitationForUser.id)}
        />
      )}

      {/* Invite form (proposer only) */}
      {isProposer && onInvite && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Invite Contributors
          </h4>
          <InviteForm onInvite={onInvite} />
        </div>
      )}

      {/* Pending invitations */}
      {isProposer && pendingInvitations.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Pending Invitations ({pendingInvitations.length})
          </h4>
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => (
              <InvitationCard key={invitation.id} invitation={invitation} />
            ))}
          </div>
        </div>
      )}

      {/* Contributors list */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
          <Users className="h-4 w-4" />
          Contributors ({participants.length})
        </h4>
        {participants.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No contributors yet
          </p>
        ) : (
          <div className="space-y-2" data-testid="contributors-list">
            {participants.map((participant) => (
              <ContributorCard key={participant.id} participant={participant} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContributorManagement;
